import DynamicProppingAncestor from '../DynamicProppingAncestor';
import productService from '../../services/products';
import addItem from '../../state/tac/tacReducer/addItem';
import updateDependentItems from '../../state/tac/tacReducer/updateDependentItems';
import updateItem from '../../state/tac/tacReducer/updateItem';
import tacHelpers from '../../state/tac/tacHelpers';
import constants from '../../settings/constants';
import idGenerator from '../../util/aactools/idGenerator';
import { replace } from '../../state/tac/replace';
import * as PIXI from 'pixi.js-legacy';
import { ITEMS } from '../../constants';

export default class Section extends DynamicProppingAncestor {
  /**
   * Sorts the the sections children (drawing order) according to specific rules.
   * Overrides implementation in Item
   * @returns {undefined}
   */
  sortChildren() {
    const postWidth = productService.getPostWidth();

    this.childrenContainer.children.forEach(child => {
      if (productService.isType(child.item, 'cross-brace')) {
        child.zIndex = 1;
      } else if (productService.isType(child.item, 'side-panel')) {
        if (child.item.id.includes('_back')) {
          // Back posts first, order of left and right one doesn't matter
          child.zIndex = 2;
        } else if (
          child.item.id.includes('_middle') &&
          child.item.x < postWidth
        ) {
          // Then the left connector
          child.zIndex = 3;
        } else if (
          child.item.id.includes('_front') &&
          child.item.x < postWidth
        ) {
          // Then the left front post
          child.zIndex = 4;
        } else if (
          child.item.id.includes('_middle') &&
          child.item.x > postWidth
        ) {
          // Then we reserve a number of z-indexes for the inserts,
          // before adding the right connector
          child.zIndex = 10000;
        } else if (
          child.item.id.includes('_front') &&
          child.item.x > postWidth
        ) {
          // And finally the right front post
          child.zIndex = 10001;
        }
      } else if (productService.isType(child.item, ITEMS.DOOR)) {
        // Doors should be drawn on top of everything else in the section
        child.zIndex = 10002;
      } else {
        // Inserts need to have z-indexes between 4 and 10000,
        // and sorted internally through their y position
        child.zIndex = 5 + child.item.y;
      }
    });
    this.childrenContainer.sortChildren();
  }

  drawChildren() {
    super.drawChildren();
    const postWidth = productService.getPostWidth();

    if (!tacHelpers.isWithinWall(this.item, this.app.tac)) {
      // If we are being dragged around outside the scene, display all parts
      this.childrenContainer.children.forEach(child => {
        child.alpha = 1;
      });
      return;
    }

    this.childrenContainer.children.forEach(child => {
      child.alpha = 1;
      if (
        productService.isType(child.item, 'side-panel') &&
        child.item.x < postWidth &&
        tacHelpers
          .findSuperSection(this.app.itemContainer.tac, this.item, [])
          .some(item => item.x < this.item.x)
      ) {
        // Hide the left side unit when we have a section to the left of us.
        child.alpha = 0;
      }
    });

    if (
      !this.childrenContainer.children.some(
        child =>
          productService.isType(child.item, 'side-panel') &&
          child.item.x > postWidth
      )
    ) {
      // If we are missing the right side unit in the TAC
      // we need to borrow it from the section next to us
      const rightNeighbour = tacHelpers
        .findSuperSection(this.app.itemContainer.tac, this.item, [])
        .find(item => item.x > this.item.x);

      if (rightNeighbour) {
        // Get the left side unit items from our neighbour and make it our right side unit
        rightNeighbour.items
          .filter(
            item =>
              productService.isType(item, 'side-panel') && item.x < postWidth
          )
          .forEach(sidePanelItem => {
            this.addItem({
              ...sidePanelItem,
              x: sidePanelItem.x + (this.item.width - postWidth),
              itemid: idGenerator.fakeId(),
            });
          });
      }
    }
  }

  update(args) {
    super.update(args);
    this.shadow && this.setShadowDimensions();
    this.childrenContainer &&
      this.childrenContainer.children.forEach(child => {
        if (child.item && tacHelpers.isPartOf(child.item, this.item)) {
          child.interactive = false;
        }
      });
  }

  clearCache() {
    this.dependencyUpdatedTac = null;
  }

  needsFullDependencyReCalc() {
    /*
    If we haven't calculated dependencies before during the drag event,
    or we have altered the super section (with more members than just me)
    we need a full re-calc of all dependencies
    */
    return (
      !this.dependencyUpdatedTac ||
      (this.mySuperSection !== this.previousSuperSection &&
        [this.mySuperSection, this.previousSuperSection].some(
          superSection => superSection.length > 1
        ))
    );
  }

  getDependencyUpdates(newItem, updatedTac, triggerItem) {
    if (this.needsFullDependencyReCalc()) {
      this.dependencyUpdatedTac = updateDependentItems(updatedTac, {
        triggerItem,
      });
    } else {
      // If not we can just return the previously calculated tac,
      // replacing the item currently being dragged with the newest version
      const isFake = !idGenerator.hasRealId(newItem);
      replace(
        this.dependencyUpdatedTac.items,
        updatedTac.items.find(
          item =>
            item.itemid === newItem.itemid ||
            (isFake && !idGenerator.hasRealId(item))
        ),
        { replaceFakes: isFake }
      );
    }

    return this.dependencyUpdatedTac;
  }

  /**
   * Updates the dependents of a specific section during a dragging event
   * @param {*} newItem
   * @returns {undefined}
   */
  updateDependentItemsDragging(newItem) {
    let updatedTac;

    this.previousSuperSection = this.mySuperSection;
    this.mySuperSection = tacHelpers.findSuperSection(this.app.tac, newItem);
    const isAlone = this.mySuperSection.length === 1;

    if (newItem.itemid) {
      // moving an item already on the scene
      updatedTac = updateItem(this.app.tac, newItem, this.app.tac, {
        // Optimization: If we now that we are alone in the super section,
        // we can safely force-connect all parts on ourselves
        forceParts: isAlone,
      });

      updatedTac = this.getDependencyUpdates(
        newItem,
        updatedTac,
        this.app.dragging.originalItem
      );
    } else {
      // when dragging from swiper
      updatedTac = addItem(this.app.tac, newItem, this.app.tac, {
        // Optimization: If we now that we are alone in the super section,
        // we can safely force-connect all parts on ourselves
        forceParts: isAlone,
      });

      updatedTac = this.getDependencyUpdates(newItem, updatedTac, newItem);

      // a new sprite will be created from the tmp item added to the fake tac,
      // so let's detach from our parent to avoid a conflict
      this.app.itemContainer.removeChild(this);
    }

    this.app.itemContainer.update({
      room: this.app.room.model,
      options: this.app.itemContainerOptions,
      ratio: this.app.room.ratio,
      tac: updatedTac,
      updateOrigo: false,
    });
  }

  getDropAreaSprite(localSlot, width, allSlots) {
    if (
      allSlots.some(
        slot => slot.y + constants.DISTANCE_BETWEEN_ATTACHMENTS === localSlot.y
      )
    ) {
      /*
      Special case for the top two slots in the 1785 high IVAR sections:
      to stop these from overlapping, we move the top one a bit further up
      */
      localSlot.height += constants.DISTANCE_BETWEEN_ATTACHMENTS;
    }
    const sprite = super.getDropAreaSprite(localSlot, width);

    if (localSlot.height * this.ratio > sprite.height) {
      sprite.y = (this.item.height - localSlot.y) * this.ratio - sprite.height;
    }
    return sprite;
  }

  getSectionWidthAndHeight() {
    const { width, height } = this.data.faceSize;
    return [width * this.ratio, height * this.ratio];
  }

  getShadowWidthAndHeight(scaledVertices) {
    const [sectionWidth] = this.getSectionWidthAndHeight();

    return {
      width: sectionWidth - productService.getPostWidth() * this.ratio * 2,
      height: scaledVertices[3][1] / 2 + scaledVertices[4][0],
    };
  }

  setShadowDimensions() {
    const scaledVertices = this.data.scaledVertices;
    const sectionDimensions = this.getSectionWidthAndHeight();
    const { width, height } = this.getShadowWidthAndHeight(scaledVertices);

    this.shadow.height = height;
    this.shadow.width = width;

    this.shadow.position.set(
      scaledVertices[4][0] + productService.getPostWidth() * this.ratio,
      sectionDimensions[1]
    );
  }

  createSectionShadow() {
    const scaledVertices = this.data.scaledVertices;
    const { width, height } = this.getShadowWidthAndHeight(scaledVertices);

    this.shadow = new PIXI.Graphics();
    this.shadow.beginFill(0x000000, 0.2);
    this.shadow.drawRect(0, 0, width, height);
    this.setShadowDimensions();
    this.shadow.endFill();
    this.shadow.skew.x = -20;

    this.addChild(this.shadow);
  }

  isBeingDragged() {
    if (!this.app.dragging) return false;

    const matchingIds = this.app.dragging.item.itemid === this.item.itemid;
    return matchingIds;
  }

  setShadowVisibility(visible) {
    this.shadow.alpha = visible ? 1 : 0;
  }

  draw() {
    !this.shadow && this.createSectionShadow();
    const shadowVisible = !this.isBeingDragged();
    this.setShadowVisibility(shadowVisible);
    super.draw();
    this.sprite.alpha = 0;
  }

  excludeChildFromHitArea(child) {
    return child.item && tacHelpers.isPartOf(child.item, this.item);
  }

  /**
   * Removes the drop areas. Overrides implementation in Item.
   * @returns {undefined}
   */
  eraseDropAreas() {
    !this.app.dragging && super.eraseDropAreas();
  }

  /**
   * Handles an old sprite that no longer is a part of the section.
   * Overrides implementation in Item.
   * @param {*} sprite
   * @returns {undefined}
   */
  handleOldSprite(sprite) {
    sprite !== this.app.dragging?.sprite && sprite.destroy();
  }
}
